/**
 * Static mapping for exemption category labels and reason code labels + descriptions.
 * Keys match the FlexTerra API response (categoryKey / reasonCodeKey).
 * Reference: https://code.amazon.com/packages/HarmonyFlexStanding/blobs/mainline/--/src/util/ExemptionMappingUtil.ts
 */

export interface CategoryOption {
    value: string;
    label: string;
}

export interface ReasonCodeOption {
    value: string;
    label: string;
    description?: string;
}

export const EXEMPTION_CATEGORIES: CategoryOption[] = [
    {value: 'UNSAFE', label: 'Unsafe Conditions'},
    {value: 'LSPE_LHE', label: 'LHE/LSPE'},
    {value: 'NO_SHOW_LATE_CANCEL', label: 'No Show/Late Cancel'},
    {value: 'UNDELIVERED_PACKAGE', label: 'Undelivered Package/Late Delivery'},
    {value: 'UNATTENDED_DNR', label: 'Unattended DNR'},
    {value: 'RETURN_TO_STATION', label: 'Return to Station'},
    {value: 'LIFE_HAPPENS_JP', label: 'Life Happens JP'},
];

export const REASON_CODES: Record<string, ReasonCodeOption[]> = {
    UNSAFE: [
        {value: 'LSPE_UNSAFE_SNOW', label: 'LSPE Unsafe Snow', description: 'DP is reporting there is severe weather with SNOW impacting them delivering, or returning packages.'},
        {value: 'LSPE_UNSAFE_COLLISION', label: 'LSPE Unsafe Collision', description: 'DP is reporting they were in a collision that kept them from delivering, or returning packages.'},
        {value: 'LSPE_UNSAFE_INJURY', label: 'LSPE Unsafe Injury', description: 'DP is reporting they were injured (minor) while delivering and will not be able to finish their route/return packages.'},
        {value: 'LSPE_UNSAFE_ANIMALS', label: 'LSPE Unsafe Animals', description: 'DP reports the presence of pet(s) at location causing them to feel uncomfortable or concerned for their safety, or an animal attack that kept them from delivering, or returning packages.'},
        {value: 'LSPE_UNSAFE_LOCKED_IN', label: 'LSPE Unsafe Locked In', description: 'DP is reporting that while making deliveries they were unable to exit customer location or Amazon facility.'},
        {value: 'LSPE_UNSAFE_THREAT', label: 'LSPE Unsafe Threat', description: 'Driver reports direct threat from a human being, verbal and/or physical. Driver reports harassment, intimidation, or discrimination (racially, or sexually).'},
        {value: 'LSPE_UNSAFE', label: 'LSPE Unsafe', description: 'Driver felt generally unsafe, no specific incident, due to environmental issues.'},
        {value: 'LSPE_UNSAFE_LOCATION', label: 'LSPE Unsafe Location', description: 'DP did not feel safe due to delivery location.'},
        {value: 'LSPE_UNSAFE_THEFT', label: 'LSPE Unsafe Theft', description: 'Packages stolen from DPs vehicle/DPs personal belongings stolen.'},
        {value: 'LSPE_UNSAFE_POLICE', label: 'LSPE Unsafe Police', description: 'DP witnessed a crime and was stopped/questioned by police.'},
        {value: 'LSPE_SEVERE_WEATHER', label: 'LSPE Severe Weather', description: 'DP is reporting that there is severe weather impacting them delivering or returning packages.'},
    ],
    LSPE_LHE: [
        {value: 'LSPE_ON_ROAD_TRAFFIC_CONSTRUCTION', label: 'LSPE On Road Traffic Construction', description: 'DP is reporting they encountered road closure/traffic issues while delivering.'},
        {value: 'LSPE_ROUTING_TOO_LONG', label: 'LSPE Routing Too Long', description: 'DP is reporting that they were given too many packages or the type of deliveries made the route infeasible.'},
        {value: 'LSPE_STATION_ROUTE_TOO_BIG', label: 'LSPE Station Route Too Big', description: 'DP is reporting that they cannot fit all the packages in to their route at the time of pick up.'},
        {value: 'LSPE_APP_CRASH', label: 'LSPE App Crash', description: 'DP is reporting that the app crashed or they are receiving an in app error message that is preventing them from attending to their scheduled block, picking up packages, completing all deliveries, or performing the return leg of a block.'},
        {value: 'LSPE_APP_CHECK_IN_ISSUE', label: 'LSPE App Check In Issue', description: 'DP is reporting that they are having issues at check-in that is preventing them from picking up or attending to their scheduled block.'},
        {value: 'LSPE_APP_ISSUE_ITINERARY_ROUTING', label: 'LSPE App Issue Itinerary Routing', description: 'DP is reporting that an Itinerary or Routing Issues in App are preventing them from completing all deliveries and/or completing the return leg of their block.'},
        {value: 'LSPE_LHE_DEVICE_ACCESS', label: 'LSPE LHE Device Access', description: 'DP is reporting they lost access to their device due to damage, stolen, phone battery died, etc. during the block. OR DP is reporting they cannot access the app on their device.'},
        {value: 'LSPE_LHE_ILLNESS_INJURY', label: 'LSPE LHE Illness Injury', description: 'DP is reporting they have a medical issue/COVID that will keep them from delivering or returning packages.'},
        {value: 'LSPE_LHE_PERSONAL_OBLIGATION', label: 'LSPE LHE Personal Obligation', description: 'DP is reporting that they are not able to deliver during a block, or returning packages due to a personal obligation.'},
        {value: 'LSPE_LHE_INTERPERSONAL_VIOLENCE', label: 'LSPE LHE Interpersonal Violence', description: 'DP is dealing with an issue stemming from a DV situation.'},
        {value: 'LSPE_LHE_VEHICLE_ACCIDENT', label: 'LSPE LHE Vehicle Accident', description: 'DP is reporting they were in an accident or an accident kept them from checking in/picking up.'},
        {value: 'LSPE_LHE_TRAFFIC', label: 'LSPE LHE Traffic', description: 'DP is reporting they were going to be on time for the block but traffic on the way to the station made them late/unable to check in.'},
        {value: 'LSPE_LHE_VEHICLE_ISSUE', label: 'LSPE LHE Vehicle Issue', description: 'DP is reporting flat tire, unavailability of Fuel, Car broke down, Car battery died, overheating that led to them not being able to complete deliveries on time.'},
        {value: 'LSPE_AVD_REFUSAL', label: 'LSPE AVD Refusal', description: 'DP is reporting that they would like to opt out of delivering the age verified packages in their block.'},
    ],
    NO_SHOW_LATE_CANCEL: [
        {value: 'OLD_DEFECT_FOUND', label: 'Old defect found'},
        {value: 'POST_OFFBOARDING_DEFECT', label: 'Post-Offboarding Defect'},
        {value: 'ALERT_NOT_SENT', label: 'Alert Not Sent'},
        {value: 'DL_SCAN', label: 'DL Scan'},
        {value: 'LSE', label: 'LSE'},
    ],
    UNDELIVERED_PACKAGE: [
        {value: 'UNABLE_TO_VERIFY_OLD_CASE', label: 'Unable to verify - Old case'},
        {value: 'ACCESS_ISSUE_CUSTOMER_UNAVAILABLE', label: 'Access issue or Customer unavailable'},
        {value: 'UNDELIVERABLE_DAMAGED', label: 'Undeliverable-Damaged'},
        {value: 'UNABLE_TO_VERIFY_NEW_CASE', label: 'Unable to verify - New case'},
    ],
    UNATTENDED_DNR: [
        {value: 'CUSTOMER_SERVICE_MISCODING', label: 'Customer service miscoding'},
        {value: 'CUSTOMER_REPORTED_LOST_LATER_FOUND', label: 'Customer reported Lost but later found'},
        {value: 'ITEM_RETURNED_TO_FC', label: 'Item Returned to FC'},
        {value: 'SUSPECTED_CONCESSIONS_ABUSE', label: 'Suspected Concessions Abuse'},
        {value: 'CUSTOMER_ENTERED_INCORRECT_ADDRESS', label: 'Customer Entered Incorrect Address'},
        {value: 'DNR_CONCEDED_BEFORE_DELIVERY', label: 'DNR conceded before delivery'},
        {value: 'CANT_DETERMINE_EXACT_SHIPMENT', label: "Can't determine the exact shipment"},
        {value: 'UNABLE_TO_VERIFY_NEW_CASE', label: 'Unable to verify - New case'},
        {value: 'UNABLE_TO_VERIFY_OLD_CASE', label: 'Unable to verify - Old case'},
    ],
    RETURN_TO_STATION: [
        { value: 'RTS_US_GSF_ALCOHOL', label: 'RTS_US GSF_Alcohol' },
        { value: 'RTS_SCC_OBJECT_MISSING', label: 'RTS_SCC_Object_missing' },
        { value: 'RTS_DP_REASSIGNMENT', label: 'RTS_DP_Reassignment' },
        { value: 'RTS_SCC_RETURNED_SCAN', label: 'RTS_SCC returned scan' },
        { value: 'RTS_CSC_CX_RECEIVED', label: 'RTS_CSC_CX_received' },
        { value: 'RTS_CSC_DP_RETURN_PROOF', label: 'RTS_CSC_DP Return Proof' },
        { value: 'RTS_SDS_SUGGESTED_TO_DISPOSE', label: 'RTS_SDS suggested to Dispose' },
        { value: 'RTS_GROCERY_DAMAGE', label: 'RTS_Grocery_Damage' },
        { value: 'RTS_LP_EXEMPTION', label: 'RTS_LP_Exemption' },
        { value: 'RTS_PRE_AUDIT_EXEMPTION', label: 'RTS_Pre-Audit Exemption' },
        { value: 'RTS_TECHNICAL_ISSUES', label: 'Technical Issues' }
    ],
    LIFE_HAPPENS_JP: [
        {value: 'LIFE_HAPPENS_JP', label: 'Life Happens (Japan FPA)', description: 'DP delivers in Japan and is stating that they have a child or family related issue that prevents them from picking up, delivering or completing the return leg of their scheduled block.'},
    ],
};

/**
 * Resolve a category key to a human-readable label.
 */

export const getCategoryLabel = (categoryKey: string): string | undefined => {
    const found = EXEMPTION_CATEGORIES.find(c => c.value === categoryKey);
    return found?.label;
};

/**
 * Resolve a reason code key to label + description.
 */

export const getReasonCodeOption = (reasonCodeKey: string, categoryKey: string): ReasonCodeOption => {
    const reasons = REASON_CODES[categoryKey];
    if (reasons) {
        const found = reasons.find(r => r.value === reasonCodeKey);
        if (found) return found;
    }
    return {value: reasonCodeKey, label: reasonCodeKey};
};
